import React, { useEffect, useMemo, useState } from "react";
import { client } from "../config/prismic";

const readText = (field, fallback = "TBA") =>
  field?.[0]?.text?.trim() || fallback;

const Dates = () => {
  const [dates, setDates] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const response = await client.getByType("dates");
      setDates(response.results[0]?.data || null);
    };

    fetchData();
  }, []);

  const timeline = useMemo(() => {
    if (!dates) return [];

    return [
      {
        title: "Abstract Submission",
        value: readText(dates.paper_submission),
        badge: "Step 1",
      },
      {
        title: "Paper Submission Deadline",
        value: readText(dates.paper_submission_end_date),
        badge: "Step 2",
      },
      {
        title: "Review and Revision",
        value: readText(dates.review_and_revision_date),
        badge: "Step 3",
      },
      {
        title: "Acceptance Notification",
        value: readText(dates.acceptance),
        badge: "Step 4",
      },
      {
        title: "Camera-Ready Paper",
        value: readText(dates.camera_ready_paper),
        badge: "Step 5",
      },
      {
        title: "Author Registration",
        value: readText(dates.last_date_registration),
        badge: "Step 6",
      },
      {
        title: "Conference Dates",
        value: readText(dates.conference_date),
        badge: "Step 7",
      },
    ];
  }, [dates]);

  if (!dates) {
    return (
      <section id="dates" className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl rounded-3xl border border-slate-100 bg-slate-50 p-6 md:p-8">
            <div className="h-6 w-36 animate-pulse rounded bg-white/10" />
            <div className="mt-3 h-9 w-64 animate-pulse rounded bg-white/10" />
            <div className="mt-8 grid gap-3 md:grid-cols-2">
              {[...Array(4)].map((_, idx) => (
                <div
                  key={idx}
                  className="h-24 animate-pulse rounded-2xl bg-slate-100"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="dates"
      className="relative overflow-hidden bg-white py-16 md:py-20"
    >
      <div className="pointer-events-none absolute -top-32 -left-20 h-80 w-80 rounded-full bg-[#d90845]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />

      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl rounded-3xl border border-slate-100 bg-slate-50/50 p-5 shadow-xl backdrop-blur-md md:p-8">
          <div className="text-center">
            <span className="inline-flex rounded-full border border-[#d90845]/40 bg-[#d90845]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#ff5a88] md:text-sm">
              Dates
            </span>
            <h2 className="mt-4 text-2xl font-extrabold text-slate-900 md:text-3xl tracking-tight leading-tight">
              Important Timeline
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-xs leading-relaxed text-slate-600 md:text-sm">
              Track each conference milestone and stay on schedule for
              submission, review, and registration.
            </p>
          </div>

          <div className="relative mt-8 pl-0 sm:pl-10">
            <div className="absolute left-4 top-0 hidden h-full w-[2px] bg-slate-100 sm:block" />

            <div className="space-y-3 md:space-y-4">
              {timeline.map((item) => (
                <div
                  key={item.title}
                  className="group relative rounded-2xl border border-slate-200 bg-white p-4 transition-all duration-300 hover:border-[#d90845]/60 hover:shadow-lg md:p-5"
                >
                  <div className="absolute -left-[2.05rem] top-1/2 hidden h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-slate-200 group-hover:bg-pink transition-colors duration-300 sm:block" />
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pink opacity-80">
                          {item.badge}
                        </p>
                        <h3 className="text-lg font-bold text-slate-900 md:text-lg tracking-tight">
                          {item.title}
                        </h3>
                      </div>
                      <div className="inline-flex w-fit rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-2 text-xs font-bold text-slate-800 md:text-sm shadow-sm">
                        {item.value}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dates;
